import {
  ArticleReferences,
  DetailHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Markdown,
  READING_MEASURE,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { ArticleCompactHeader } from "~/components/news/ArticleCompactHeader";
import { ArticleGallery } from "~/components/news/ArticleGallery";
import { ArticleVideo } from "~/components/news/ArticleVideo";
import { RESUME_PARAM } from "~/components/news/ContinueSection";
import { SaveButton } from "~/components/news/SaveButton";
import { ContentDisclaimer } from "~/components/ui/ContentDisclaimer";
import { Screen } from "~/components/ui/Screen";
import { SNACKBAR_POSITION_TOP } from "~/components/ui/snackbar";
import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import {
  ApiError,
  fetchArticle,
  fetchProgress,
  recordProgress,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { topicLabel } from "~/lib/taxonomy";
import { useDwellTime } from "~/lib/use-dwell-time";
import { useFireOnce } from "~/lib/use-fire-once";
import { useReadingPosition } from "~/lib/use-reading-position";
import { useScrollDepth } from "~/lib/use-scroll-depth";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";
import type { NewsArticleDetail } from "~/types/news";
import type { ContentProgress, ContentProgressUpdate } from "~/types/progress";

/** Past this share of a video, the member has watched it. */
const VIDEO_COMPLETE_FRACTION = 0.97;

/**
 * What the header browses: the cover, then the gallery. An editor can put a
 * gallery photo on as the cover too, and the member should not page past it twice.
 */
function headerImages(article: NewsArticleDetail): string[] {
  const images = [article.cover_image_url, ...article.gallery_image_urls];
  return [...new Set(images.filter((url): url is string => Boolean(url)))];
}

/**
 * A single article.
 *
 * Constrained to `md` rather than the feed's `xl`, and the body is capped at a reading
 * measure inside that — a 900px line of prose is uncomfortable however much room the
 * window has.
 */
export function ArticleRoute() {
  const { t } = useTranslation();
  const { locale, formatDate } = useLocale();
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id ?? null);

  // Handed to the compact header, which reveals itself as this element leaves.
  const titleRef = useRef<HTMLHeadingElement>(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.article(locale, slug),
    queryFn: ({ signal }) => fetchArticle(slug, locale, signal),
    enabled: slug.length > 0,
  });

  // The body element, measured for scroll depth.
  const bodyRef = useRef<HTMLDivElement>(null);

  // Where the member stopped last time. Read before the video mounts and before the
  // reading position starts saving, so the first save cannot overwrite the place to
  // come back to with the top of the page.
  const progress = useQuery({
    queryKey: queryKeys.articleProgress(userId ?? "", slug),
    queryFn: ({ signal }) => fetchProgress(slug, signal),
    enabled: Boolean(userId) && slug.length > 0,
    staleTime: Infinity,
  });
  const progressReady = !userId || progress.isSuccess || progress.isError;
  const resume = searchParams.get(RESUME_PARAM) === "1";

  const save = useMutation({
    mutationFn: (update: ContentProgressUpdate) => recordProgress(slug, update),
    onSuccess: (saved: ContentProgress) => {
      queryClient.setQueryData(
        queryKeys.articleProgress(userId ?? "", slug),
        saved,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.continueList(userId ?? "", locale),
      });
    },
  });
  const report = (update: ContentProgressUpdate) => {
    if (userId) save.mutate(update);
  };

  useReadingPosition(bodyRef, {
    key: progressReady ? (data?.id ?? null) : null,
    initialFraction: progress.data?.scroll_fraction ?? null,
    restore: resume,
    onSave: (fraction) => report({ scroll_fraction: fraction }),
  });

  // Keyed on the resolved id, so the pending, 404 and error branches all pass null
  // and a failed open can never be counted as a read.
  const viewKey = data?.id ?? null;
  const subject = {
    subjectType: "news" as const,
    subjectId: data?.id ?? null,
    subjectKey: data?.slug ?? null,
    contentFormat: data?.format ?? null,
  };

  useFireOnce(viewKey, () => {
    track(AnalyticsEventName.ContentView, subject);
  });

  useDwellTime({
    key: viewKey,
    onSettle: (milliseconds, viewId, reason) => {
      track(AnalyticsEventName.ContentDwell, {
        ...subject,
        value: milliseconds,
        props: { view_id: viewId, reason },
      });
    },
  });

  useScrollDepth(bodyRef, viewKey, (depth) => {
    track(AnalyticsEventName.ContentScroll, { ...subject, value: depth });
  });

  const backTo = "/news";
  const backLabel = t("article.backToFeed");

  const backLink = (
    <DetailHeader
      backTo={backTo}
      backLabel={backLabel}
      backTestId="article.back"
    />
  );

  if (isPending) {
    return (
      <Screen maxWidth="md">
        <LoadingState />
      </Screen>
    );
  }

  // A missing article is an ordinary outcome — a stale link, or a slug that only exists
  // in the other language — not an error worth a retry button.
  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <Screen maxWidth="md">
        <EmptyState message={t("article.notFound")} />
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen maxWidth="md">
        <ErrorState
          message={error instanceof Error ? error.message : undefined}
          onRetry={() => void refetch()}
        />
      </Screen>
    );
  }

  return (
    <>
      <ArticleCompactHeader
        title={data.title}
        backTo={backTo}
        backLabel={backLabel}
        revealAfterRef={titleRef}
      />

      <Screen maxWidth="md">
        <Box
          component="article"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 4 },
          }}
        >
          {backLink}

          <ArticleGallery slug={data.slug} images={headerImages(data)} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxWidth: READING_MEASURE,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography
                variant="L3"
                sx={{
                  color: "text.muted",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                <time dateTime={data.published_at}>
                  {formatDate(data.published_at)}
                </time>
              </Typography>
              {userId ? (
                <SaveButton
                  slug={data.slug}
                  userId={userId}
                  articleId={data.id}
                  testId="article.save"
                  sx={{
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              ) : null}
            </Box>

            <Typography
              ref={titleRef}
              variant="H1"
              sx={{ color: "text.primary" }}
              data-testid="article.title"
            >
              {data.title}
            </Typography>

            {/* The standfirst borrows the H3 *style* but is not a heading — rendering it
              as one would skip from h1 to h3 in the document outline. */}
            {data.excerpt ? (
              <Typography
                component="p"
                variant="H3"
                sx={{ color: "text.secondary" }}
              >
                {data.excerpt}
              </Typography>
            ) : null}

            {data.topics.length > 0 ? (
              <Box
                component="ul"
                aria-label={t("article.topics")}
                data-testid="article.topics"
                sx={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {data.topics.map((topic) => (
                  <Box component="li" key={topic}>
                    <Chip
                      label={topicLabel(t, topic)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>

          {/* Mounted only once the stored position is known: the player reads its
              start on mount, and a video that opened at zero cannot be told later. */}
          {data.video_youtube_id && progressReady ? (
            <ArticleVideo
              youtubeId={data.video_youtube_id}
              title={data.title}
              subjectId={data.id}
              subjectKey={data.slug}
              contentFormat={data.format}
              startSeconds={progress.data?.video_position_seconds ?? 0}
              onPosition={({ positionSeconds, durationSeconds }) =>
                report({
                  video_position_seconds: Math.floor(positionSeconds),
                  completed:
                    positionSeconds / durationSeconds >=
                    VIDEO_COMPLETE_FRACTION,
                })
              }
              onComplete={() => report({ completed: true })}
            />
          ) : null}

          <Box
            ref={bodyRef}
            sx={{
              maxWidth: READING_MEASURE,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 4, md: 5 },
            }}
          >
            <Markdown>{data.body}</Markdown>
            <ArticleReferences
              heading={t("article.references")}
              references={data.references}
              copy={{
                label: t("article.copyReference"),
                message: t("article.copied"),
                snackbarSx: SNACKBAR_POSITION_TOP,
              }}
            />
          </Box>

          {/* Outside `bodyRef`: scroll depth measures the article, not its footer. */}
          <ContentDisclaimer />
        </Box>
      </Screen>
    </>
  );
}
